import {
  AdminHeader,
  AdminOptionsDropdown,
  Button,
  Div,
  MenuNamesResponse,
  Tab,
  TabsWrapper,
  getLocationStoreName,
  isAdmin,
  renderStoreName,
  useUIState,
  reportError,
  MerchantBillingNotices,
} from '@vgrubs/components';
import { useLocationBrandPicker } from 'components/src/store/useLocationBrandPicker';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ManageProviders } from './ManageProviders';
import { ManagePOS } from './Pos/ManagePos';
import {
  IntegrationPlaybookEntry,
  IntegrationPlaybookModal,
  ProvidersTab,
} from './IntegrationPlaybook';

enum Tabs {
  Providers = 1,
  POS,
}

export const Providers = () => {
  const pathname = useLocation().pathname;
  const { id, locId } = useParams() as { id: string; locId: string };
  const isLocations = pathname.startsWith('/locations/manage-providers');
  const pathPrefix: 'locations' | 'brands' = isLocations
    ? 'locations'
    : 'brands';
  const locationId = isLocations ? id : locId;
  const brandId = !isLocations ? id : '0';

  const { setSelectedLocation, setSelectedBrand } = useLocationBrandPicker();

  const [storeName, setStoreName] = useState<MenuNamesResponse | null>(null);

  const { providersTab, setProvidersTab } = useUIState((state) => state);

  const [playbookOpen, setPlaybookOpen] = useState(false);
  const openPlaybook = () => setPlaybookOpen(true);
  const switchTab = (tab: ProvidersTab) =>
    setProvidersTab(tab === 'pos' ? Tabs.POS : Tabs.Providers);

  useEffect(() => {
    getStoreName();
  }, []);

  async function getStoreName() {
    try {
      const { data } = await getLocationStoreName(locationId, brandId);
      setStoreName(data.data);
    } catch (error) {
      reportError(error);
    }
  }

  const renderStore = storeName ? renderStoreName(storeName) : '';
  const navigate = useNavigate();

  const actions = [
    {
      label: 'Menu',
      onClick: () => navigate(`/menu/${locationId}/location/${brandId}/brand`),
    },
    {
      label: 'Working Hours',
      onClick: () => {
        navigate(`/working-hours`);
        setSelectedLocation(storeName?.location || null);
        setSelectedBrand(storeName?.brand || null);
      },
    },
  ];

  return (
    <>
      <AdminHeader title={'Manage Providers'} subtitle={renderStore}>
        <Div $flex $gap="16px" $desktop>
          {actions.map((action) => (
            <Button
              key={action.label}
              $selected
              $w="150px"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </Div>
        <Div $mobile>
          <AdminOptionsDropdown actions={actions} />
        </Div>
      </AdminHeader>
      {/* This location's billing banners in the merchant app; nothing in
          admin or on a brand's providers. */}
      {isLocations && (
        <MerchantBillingNotices locationId={Number(locationId)} />
      )}
      {pathname.includes('manage-providers') && (
        <>
          {isLocations ? (
            <>
              <TabsWrapper>
                <Tab
                  selected={providersTab === Tabs.Providers}
                  onClick={() => setProvidersTab(Tabs.Providers)}
                >
                  Marketplace
                </Tab>
                <Tab
                  selected={providersTab === Tabs.POS}
                  onClick={() => setProvidersTab(Tabs.POS)}
                >
                  POS
                </Tab>
              </TabsWrapper>

              {/* Admin only: connecting a POS and moving marketplaces onto
                  FoodHub is vGrubs' own job. A tablet location gets it too,
                  because that is the one that turns into a POS location the
                  day the client asks. */}
              {isAdmin && <IntegrationPlaybookEntry onOpen={openPlaybook} />}

              {providersTab === Tabs.Providers && (
                <ManageProviders
                  locationId={locationId}
                  brandId={brandId}
                  storeName={storeName}
                  pathPrefix={pathPrefix}
                />
              )}

              {providersTab === Tabs.POS && (
                <ManagePOS
                  locationId={locationId}
                  brandId={brandId}
                  storeName={storeName}
                  pathPrefix={pathPrefix}
                  onOpenPlaybook={isAdmin ? openPlaybook : undefined}
                />
              )}

              {isAdmin && (
                <IntegrationPlaybookModal
                  isOpen={playbookOpen}
                  closeModal={() => setPlaybookOpen(false)}
                  locationId={locationId}
                  storeName={storeName}
                  onSwitchTab={switchTab}
                />
              )}
            </>
          ) : (
            <Div $m="32px 0" $mSM="20px 0">
              {isAdmin && <IntegrationPlaybookEntry onOpen={openPlaybook} />}

              <ManageProviders
                locationId={locationId}
                brandId={brandId}
                storeName={storeName}
                pathPrefix={pathPrefix}
              />

              {isAdmin && (
                <IntegrationPlaybookModal
                  isOpen={playbookOpen}
                  closeModal={() => setPlaybookOpen(false)}
                  locationId={locationId}
                  storeName={storeName}
                  marketplacesOnly
                  onSwitchTab={switchTab}
                />
              )}
            </Div>
          )}
        </>
      )}
    </>
  );
};
